'use client';
/* eslint-disable */

import {
   Box,
   Button,
   Flex,
   IconButton,
   Text,
   useColorModeValue,
   useDisclosure,
   useBreakpointValue,
   useColorMode,
   Modal,
   VStack,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Fade,
   Icon,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, SearchIcon } from '@chakra-ui/icons';
import ViewSingleData from '../../components/ViewSingleData';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';
import 'jspdf-autotable';
import React, { useEffect, useState, useRef, useMemo, useCallback, memo } from 'react';
import { motion } from 'framer-motion';
import { MdNavigateNext, MdNavigateBefore } from 'react-icons/md';
import { Input } from '@chakra-ui/react';
import { VariableSizeList as List } from 'react-window';
import { useDispatch, useSelector } from 'react-redux';
import {
   deleteItem,
   getItemForMaster,
   getItems,
   getSingleItem,
   importItemExcel,
} from '../../../../../redux/actions/item';
import DeleteDialog from '../../components/DeleteDialog';
import { downloadPDF, downloadExcel } from '../../../../../utils/DataTableUtils';
import { useUser } from 'contexts';
import loaderAnimation from 'assets/img/auth/loding.gif';
import { postColumnsHideShow } from '../../../../../redux/actions/utility';
import { BsEye } from 'react-icons/bs';
import { useGlobal } from '../../../../../contexts';
import { userProfile } from '../../../../../redux/actions/user';
import Loader from 'views/admin/default/Loader';
import { DownloadIcon, X } from 'lucide-react';
import { BsFileEarmarkExcel } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { FiUploadCloud } from 'react-icons/fi';
import DynamicTable from '../../components/DynamicAllDataTable';
import ItemMasterForm from './ItemMasterForm';

const columnHelper = createColumnHelper();
const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

// Search component
const SearchComponent = memo(({ expanded, setExpanded, searchQuery, handleSearchChange, bg, icon, wrapperRef }) => (
   <MotionFlex
      ref={wrapperRef}
      align="center"
      bg={bg}
      borderRadius="full"
      cursor="pointer"
      px={expanded ? 3 : 0}
      w={expanded ? '200px' : '40px'}
      h="40px"
      transition="all 0.3s ease"
      onClick={() => setExpanded(true)}>
      <MotionSearchIcon
         color={icon}
         animate={{ rotate: expanded ? 0 : 90 }}
         transition={{ duration: 0.3 }}
         style={{
            marginRight: expanded ? '8px' : '10px',
            marginLeft: expanded ? '0' : '10px',
         }}
      />
      {expanded && (
         <Input
            variant="unstyled"
            placeholder="Search..."
            value={searchQuery}
            color={icon}
            onChange={handleSearchChange}
            autoFocus
         />
      )}
   </MotionFlex>
));

export default function ItemMaster({ columnsData }) {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const { user } = useUser();
   const isMobileOrTablet = useBreakpointValue({
      base: true,
      md: false,
      lg: false,
   });
   const isDesktop = useBreakpointValue({ base: false, md: true });
   // const toast = useToast();
   const { isOpen, onOpen, onClose } = useDisclosure();
   const backgroundColor = useColorModeValue('white', 'rgba(12, 20, 41, 1)');
   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');
   const companyId = Number(localStorage.getItem('companyId'));
   const Admin = user?.isAdmin || false;

   const itemRight = useMemo(() => {
      if (!user?.userRights) return null;
      return Array.isArray(user.userRights)
         ? user.userRights.find((right) => right.companyId === companyId)?.item
         : null;
   }, [user?.userRights, companyId]);

   // Redux state
   const dispatch = useDispatch();
   const itemdata = useSelector((state) => state.item?.data || []);
   const totalCount = useSelector((state) => state.item.totalCount || 0);
   const pagination = useSelector((state) => state.item?.pagination);
   // const isLoading = useSelector((state) => state.item?.loading || false);
   // const singleEntity = useSelector((state) => state?.item?.single || {});
   const [singleEntity, setSingleEntity] = useState({});
   const [isLoading, setIsLoading] = useState(false);
   const [isSearching, setIsSearching] = useState(false);
   const { userGlobalSettingsData } = useGlobal();
   const globalSetting = userGlobalSettingsData?.[0]?.askCompany?.item;
   const shouldShowCompanyDropdown = globalSetting || false;

   // Component state
   const [data, setData] = useState([]);
   const [searchQuery, setSearchQuery] = useState('');
   const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
   const [currentPage, setCurrentPage] = useState(1);
   const [limit] = useState(50);
   const [expanded, setExpanded] = useState(false);
   const [sorting, setSorting] = useState([]);
   const [currentRow, setCurrentRow] = useState(null);
   const isAbs = currentRow?.isAbs === 'true' || currentRow?.isAbs === true;
   const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [selectedRowId, setSelectedRowId] = useState(null);
   const [isEditMode, setIsEditMode] = useState(false);
   const [isOpenSingleView, setIsOpenSingleView] = useState(false);

   // Refs
   const wrapperRef = useRef();

   // FIXED: Column visibility initialization - only set once
   const [columnVisibility, setColumnVisibility] = useState(() => {
      // First try to get from user settings
      if (user?.viewJson?.length > 0) {
         const companySettings = user.viewJson.find((item) => item.companyId === companyId);
         if (companySettings?.item) {
            return companySettings.item;
         }
      }
      // Fallback to default visible columns
      const visibleColumns = ['name', 'hsn', 'itemGroupName', 'uqc1Data', 'stock'];
      return columnsData.reduce((acc, column) => {
         acc[column.id] = visibleColumns.includes(column.id);
         return acc;
      }, {});
   });

   // Debounce search
   useEffect(() => {
      const timerId = setTimeout(() => {
         setDebouncedSearchQuery(searchQuery);
      }, 500);
      return () => clearTimeout(timerId);
   }, [searchQuery]);

   // Initial data fetch
   useEffect(() => {
      if (!user?.loginhistory?.companyId) return;

      const fetchMainData = async () => {
         try {
            setIsLoading(true);
            await dispatch(
               getItems({
                  companyId: user.loginhistory.companyId,
                  page: 1,
                  status: true,
                  search: debouncedSearchQuery,
                  sortBy: sorting.length ? sorting[0]?.id : 'name',
                  sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
                  ...(shouldShowCompanyDropdown && { companyIds: user?.loginhistory?.companyId }),
               }),
            );
         } catch (error) {
            console.error('Error fetching main data:', error);
         } finally {
            setIsLoading(false);
         }
      };

      fetchMainData();
   }, [dispatch, debouncedSearchQuery, sorting, user?.loginhistory?.companyId, shouldShowCompanyDropdown]);

   // Update data when redux state changes
   useEffect(() => {
      setData(itemdata);
   }, [itemdata]);

   // REMOVED: The problematic useEffect that was resetting columnVisibility

   // Delete handler
   const handleDelete = useCallback(async () => {
      if (selectedRowId !== null) {
         try {
            await dispatch(deleteItem(selectedRowId, companyId));
            if (user && user.loginhistory.companyId) {
               dispatch(
                  getItems({
                     companyId: user.loginhistory.companyId,
                     page: currentPage,
                     status: true,
                     search: debouncedSearchQuery,
                     sortBy: sorting.length ? sorting[0]?.id : 'name',
                     sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
                     ...(shouldShowCompanyDropdown && { companyIds: user?.loginhistory?.companyId }),
                  }),
               );
            }
            setDeleteDialogOpen(false);
         } catch (err) {
            toast({
               title: 'Error',
               description: 'Failed to delete item',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      }
   }, [dispatch, selectedRowId, currentPage, debouncedSearchQuery, sorting, user, toast]);

   const handleDeleteClick = useCallback((id) => {
      setSelectedRowId(id);
      setDeleteDialogOpen(true);
   }, []);

   // Multiple delete handler
   const handleMutiDeleteClick = useCallback(
      async (ids) => {
         try {
            // Implement multiple delete logic here
            // This is just a placeholder - you'll need to implement the actual multiple delete API call
            await Promise.all(ids.map((id) => dispatch(deleteItem(id, companyId))));
            if (user && user.loginhistory.companyId) {
               dispatch(
                  getItems({
                     companyId: user.loginhistory.companyId,
                     page: currentPage,
                     status: true,
                     search: debouncedSearchQuery,
                     sortBy: sorting.length ? sorting[0]?.id : 'name',
                     sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
                     ...(shouldShowCompanyDropdown && { companyIds: user?.loginhistory?.companyId }),
                  }),
               );
            }
            toast({
               title: 'Success',
               description: 'Items deleted successfully',
               status: 'success',
               duration: 3000,
               isClosable: true,
            });
         } catch (err) {
            toast({
               title: 'Error',
               description: 'Failed to delete items',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      },
      [dispatch, companyId, user, currentPage, debouncedSearchQuery, sorting, toast],
   );

   // View single data handler
   const handleViewSingleData = useCallback(
      async (id) => {
         const singleData = itemdata?.find((item) => item.id === id);
         console.log('singleData', singleData, id, itemdata, data);
         setSingleEntity(singleData);
         // await dispatch(getSingleItem(id));
         setIsOpenSingleView(true);
      },
      [dispatch, itemdata],
   );

   // Edit handler
   const handleEditClick = useCallback(
      (row) => {
         setCurrentRow(row);
         setIsEditMode(true);
         onOpen();
         setIsOpenSingleView(false);
      },
      [onOpen],
   );

   // Save handler
   const handleSave = useCallback(() => {
      if (!user?.loginhistory?.companyId) return;
      dispatch(
         getItems({
            companyId: user.loginhistory.companyId,
            page: currentPage,
            status: true,
            search: debouncedSearchQuery,
            sortBy: sorting.length ? sorting[0]?.id : 'name',
            sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
            ...(shouldShowCompanyDropdown && { companyIds: user?.loginhistory?.companyId }),
         }),
      );
   }, [dispatch, currentPage, debouncedSearchQuery, sorting, user?.loginhistory?.companyId]);

   // Table data memoization
   const tableData = useMemo(
      () =>
         data.map((item) => ({
            ...item,
            name: item.name,
            barcode: item.barcode,
            itemGroupName: item.itemGroupName,
            stock: item.stock,
            uqc1Data: item?.uqc1Data?.name,
            uqc2Data: item?.uqc2Data?.name,
            uqc3Data: item?.uqc3Data?.name,
            primaryUqcData: item?.primaryUqcData?.name,
            salesreport: item.salesreport,
            purchasereport: item.purchasereport,
            hsn: item.hsn,
            gstpercentage: item.gstpercentage,
            id: item.id,
         })),
      [data],
   );

   // Dynamic array for single view
   const dynamicArray = useMemo(
      () => [
         { label: 'Name', value: singleEntity?.name || '-' },
         { label: 'Barcode', value: singleEntity?.barcode || '-' },
         { label: 'HSN', value: singleEntity?.hsn || '-' },
         { label: 'GSTIN', value: singleEntity?.gst || '-' },
         { label: 'Balance QTY 1', value: singleEntity?.stock || '-' },
         { label: 'Balance QTY 2', value: singleEntity?.balancequantity2 || '-' },
         { label: 'Balance QTY 3', value: singleEntity?.balancequantity3 || '-' },
         { label: 'BalJson', value: singleEntity?.balJson || [] },
      ],
      [singleEntity],
   );

   // Create table instance for DynamicTable
   const table = useReactTable({
      data: tableData,
      columns: columnsData.map((column) =>
         columnHelper.accessor(column.id, {
            header: column.header,
            cell: (info) => info.getValue(),
         }),
      ),
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      state: {
         sorting,
         columnVisibility,
      },
      onSortingChange: setSorting,
      onColumnVisibilityChange: setColumnVisibility,
   });

   const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

   const ColumnVisibilityModal = ({ isOpen, onClose, columnVisibility, setColumnVisibility, user }) => {
      const [localVisibility, setLocalVisibility] = useState(columnVisibility);
      // const toast = useToast();
      const companyId = Number(localStorage.getItem('companyId'));

      // Reset local state when modal opens OR when columnVisibility changes
      useEffect(() => {
         setLocalVisibility(columnVisibility);
      }, [isOpen, columnVisibility]);

      useEffect(() => {
         const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
               onClose();
            }
         };
         if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
         }
         return () => {
            document.removeEventListener('keydown', handleEscKey);
         };
      }, [isOpen, onClose]);

      const handleSave = async () => {
         try {
            // Prepare the updated viewJson
            const updatedViewJson = user.viewJson
               ? user.viewJson.map((item) => (item.companyId === companyId ? { ...item, item: localVisibility } : item))
               : [{ companyId, item: localVisibility }];

            // Dispatch action to save
            await dispatch(
               postColumnsHideShow({
                  userId: user?.id,
                  viewJson: updatedViewJson,
               }),
            );

            // Also update user context/global state to reflect changes immediately
            dispatch(userProfile());

            // Update local state
            setColumnVisibility(localVisibility);
            toast({
               title: 'Column settings saved successfully',
               status: 'success',
            });
            onClose();
         } catch (error) {
            toast({
               title: 'Failed to save column settings',
               status: 'error',
               description: error.message,
            });
         }
      };

      const handleBackdropClick = (e) => {
         // Only close if clicking directly on backdrop, not on modal content
         if (e.target === e.currentTarget) {
            onClose();
         }
      };

      // Don't render if modal is not open
      if (!isOpen) return null;

      return (
         <div className="fixed inset-0 z-[999] flex items-center justify-center p-4" onClick={handleBackdropClick}>
            <div
               className={`absolute inset-0 transition-colors duration-300 ${isDark ? 'bg-black/70' : 'bg-black/40'}`}
            />
            <div
               className={`relative w-full max-w-2xl transform transition-all duration-300 ${isDark
                     ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
                     : 'bg-gradient-to-br from-white via-gray-50 to-white'
                  } rounded-2xl shadow-2xl border ${isDark ? 'border-slate-700/50' : 'border-gray-200/50'
                  } overflow-hidden flex flex-col max-h-[80vh]`}
               onClick={(e) => e.stopPropagation()}>
               <div
                  className={`relative px-8 py-6 border-b flex-shrink-0 ${isDark
                        ? 'border-slate-700/50 bg-gradient-to-r from-slate-800/50 to-slate-900/50'
                        : 'border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50'
                     }`}>
                  <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Show/Hide Columns</h2>
                  <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                     Customize which columns appear in your view
                  </p>
                  <button
                     onClick={onClose}
                     aria-label="Close modal"
                     className={`absolute top-6 right-6 p-2 rounded-lg transition-all duration-200 ${isDark
                           ? 'hover:bg-slate-700/50 text-slate-400 hover:text-white'
                           : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
                        }`}>
                     <X className="w-5 h-5" />
                  </button>
               </div>
               <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="grid grid-cols-2 gap-4">
                     {Object.entries(localVisibility).map(([key, value]) => (
                        <div
                           key={key}
                           className={`group relative p-3 rounded-xl transition-all duration-300 border-2 ${value
                                 ? isDark
                                    ? 'bg-gradient-to-br from-blue-900/20 to-indigo-900/20 border-blue-500/50 shadow-lg shadow-blue-500/10'
                                    : 'bg-gradient-to-br from-blue-50/50 to-indigo-50/50 border-blue-400/50 shadow-lg shadow-blue-200/30'
                                 : isDark
                                    ? 'bg-slate-800/30 border-slate-700/30 hover:border-slate-600/50'
                                    : 'bg-white/50 border-gray-300/30 hover:border-gray-400/50'
                              } cursor-pointer hover:scale-[1.02]`}
                           onClick={() =>
                              setLocalVisibility((prev) => ({
                                 ...prev,
                                 [key]: !prev[key],
                              }))
                           }>
                           <div className="flex items-center justify-between">
                              <label
                                 className={`font-semibold text-base cursor-pointer transition-colors duration-200 capitalize ${isDark ? 'text-white' : 'text-gray-900'
                                    }`}>
                                 {key
                                    .split(/(?=[A-Z])/)
                                    .join(' ')
                                    .toLowerCase()}
                              </label>
                              <div
                                 className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 ${value
                                       ? isDark
                                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/30'
                                          : 'bg-gradient-to-r from-blue-500 to-indigo-500 shadow-lg shadow-blue-400/30'
                                       : isDark
                                          ? 'bg-slate-700'
                                          : 'bg-gray-300'
                                    }`}>
                                 <span
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300 shadow-md ${value ? 'translate-x-6' : 'translate-x-1'
                                       }`}
                                 />
                              </div>
                           </div>
                           <div
                              className={`mt-3 flex items-center gap-2 text-xs font-medium ${value
                                    ? isDark
                                       ? 'text-blue-400'
                                       : 'text-blue-600'
                                    : isDark
                                       ? 'text-slate-500'
                                       : 'text-gray-500'
                                 }`}>
                              <div
                                 className={`h-1.5 w-1.5 rounded-full ${value
                                       ? isDark
                                          ? 'bg-blue-400 shadow-lg shadow-blue-400/50'
                                          : 'bg-blue-500 shadow-lg shadow-blue-400/50'
                                       : isDark
                                          ? 'bg-slate-600'
                                          : 'bg-gray-400'
                                    }`}
                              />
                              {value ? 'Visible' : 'Hidden'}
                           </div>
                        </div>
                     ))}
                  </div>
               </div>
               <div
                  className={`px-8 py-5 border-t flex justify-end gap-3 flex-shrink-0 ${isDark
                        ? 'border-slate-700/50 bg-gradient-to-r from-slate-800/50 to-slate-900/50'
                        : 'border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-white/50'
                     }`}>
                  <button
                     onClick={onClose}
                     className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 ${isDark
                           ? 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                           : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                        }`}>
                     Cancel
                  </button>
                  <button
                     onClick={handleSave}
                     className={`px-6 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-lg ${isDark
                           ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-500/30'
                           : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-blue-400/30'
                        }`}>
                     Save
                  </button>
               </div>
            </div>
         </div>
      );
   };

   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeElement = document.activeElement;
         const isTyping =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.getAttribute('role') === 'textbox' ||
            activeElement?.isContentEditable;

         if (isTyping) return;

         if (e.key.toLowerCase() === 'n' || e.key === '+') {
            if (!isOpen && !isOpenSingleView && (Admin || itemRight?.add)) {
               e.preventDefault();
               setCurrentRow({});
               setIsEditMode(false);
               onOpen();
            }
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [!isOpen, !isOpenSingleView, Admin, itemRight?.add]);

   const [isImportModalOpen, setIsImportModalOpen] = useState(false);
   const [excelFile, setExcelFile] = useState(null);

   const handleExcelUpload = (file) => {
      setExcelFile(file);
   };

   const handleImportExcel = async () => {
      console.log('excelFile', excelFile);
      if (!excelFile) {
         toast({
            title: 'Please select an Excel file',
            status: 'warning',
            position: 'top',
         });
         return;
      }

      try {
         const formData = new FormData();
         formData.append('companyId', user?.loginhistory?.companyId); // 👈 your company id
         formData.append('excel', excelFile); // 👈 file key must match backend
         console.log('formData', formData);
         const res = await dispatch(importItemExcel(formData));
         console.log('res', res);

         if (res) {
            if (res?.skippedData?.length > 0) {
               res.skippedData.forEach((data) => {
                  toast.error(`${data.errorMessage} Item Name: ${data.NAME}`);
               });
            } else {
               toast({
                  title: 'Excel imported successfully',
                  status: 'success',
                  position: 'top',
               });
            }
         }
         setIsImportModalOpen(false);
      } catch (error) {
         console.log('error', error);
         toast({
            title: error?.message || 'Import failed',
            status: 'error',
            position: 'top',
         });
      }
   };

   const handleDownloadDemoExcel = () => {
      const link = document.createElement('a');
      link.href = '/sample/item_import_demo.xlsx';
      link.download = 'Item_Import_Sample.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
   };

   return (
      <>
         <Box bg={backgroundColor} w="100%" borderRadius="xl" border="1px solid #e2e8f0">
            <Flex
               width={{ base: '100%', md: 'auto' }}
              mb={1}
               px={{ base: '1px', md: '25px' }}
               justifyContent="space-between"
               align="center"
               overflowX="auto"
               flexWrap="nowrap"
               gap={3}>
               <Flex align="center" gap={4} flexShrink={0}>
                  <Text
                     color={textColor}
                     ml={{ base: '8px', md: 0 }}
                     fontSize={{ base: '17px', lg: '22px' }}
                     fontWeight="700"
                     lineHeight="100%">
                     Item
                  </Text>
               </Flex>
               <Flex p={3} gap={2} align="center" flexShrink={0}>
                  {!isMobileOrTablet && (Admin || itemRight?.add) && user && !user.isExpired && (
                     <button
                        onClick={() => {
                           setCurrentRow({});
                           setIsEditMode(false);
                           onOpen();
                        }}
                        className={`group relative flex items-center justify-center gap-3 py-2 px-7 rounded-xl font-medium shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 overflow-hidden border ${isDark
                              ? 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-400/40 focus:ring-cyan-200/50 border-cyan-300/30'
                              : 'bg-gradient-to-br from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-white shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-400/40 focus:ring-emerald-200/70 border-emerald-300/50'
                           }`}>
                        {/* Gradient overlay on hover */}
                        <span
                           className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out ${isDark ? 'via-cyan-100/20' : 'via-white/30'
                              }`}></span>
                        {/* Icon container with background */}
                        <span
                           className={`relative flex items-center justify-center w-6 h-6 rounded-lg backdrop-blur-sm transition-all duration-300 ${isDark
                                 ? 'bg-cyan-100/20 group-hover:bg-cyan-100/30'
                                 : 'bg-white/20 group-hover:bg-white/30'
                              }`}>
                           <svg
                              className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                           </svg>
                        </span>
                        <span className="relative text-base font-semibold">Add New</span>
                     </button>
                  )}
                  <IconButton
                     colorScheme="gray"
                     icon={<BsEye />}
                     size="md"
                     mr={1}
                     onClick={() => setIsColumnModalOpen(true)}
                  />
                  <SearchComponent
                     expanded={expanded}
                     setExpanded={setExpanded}
                     searchQuery={searchQuery}
                     handleSearchChange={(e) => setSearchQuery(e.target.value.trimStart())}
                     bg={bg}
                     icon={icon}
                     wrapperRef={wrapperRef}
                  />
                  <IconButton
                     icon={<BsFileEarmarkExcel />}
                     colorScheme="gray"
                     size="md"
                     mr={1}
                     onClick={() => setIsImportModalOpen(true)}
                     aria-label="Import Excel"
                  />
               </Flex>
            </Flex>

            {/* Replace the virtual table with DynamicTable */}
            <div className='px-4 pb-4'>
            {isLoading ? (
               <Loader />
            ) : (
               <DynamicTable
                  right={itemRight}
                  table={table}
                  data={tableData}
                  handleViewSingleData={handleViewSingleData}
                  // handleStatusChange={handleStatusUpdate}
                  // handleImageClick={handleImageClick}
                  // toggleDropdown={toggleDropdown}
                  // filter={filter}
                  emptyMessage={'No item data found'}
                  onEditClick={(row) => {
                     setCurrentRow(row);
                     setIsEditMode(true);
                     onOpen();
                     setIsOpenSingleView(false);
                  }}
                  onDeleteClick={(id) => {
                     handleDeleteClick(id);
                     setIsOpenSingleView(false);
                  }}
                  onMultiDeleteClick={(ids) => {
                     handleMutiDeleteClick(ids);
                  }}
                  canDelete={true}
                  columnVisibility={columnVisibility}
                  columnsData={columnsData}
                  sorting={sorting}
                  limit={limit}
                  setCurrentRow={setCurrentRow}
                  onOpen={onOpen}
                  setIsEditMode={setIsEditMode}
                  setIsOpenSingleView={setIsOpenSingleView}
                  setDeleteDialogOpen={setDeleteDialogOpen}
                  setSelectedRowId={setSelectedRowId}
                  debouncedSearchQuery={debouncedSearchQuery}
                  isAbs={isAbs}
                  entityType={'item'}
               />
            )}
            </div>

            <React.Suspense fallback={'loading...'}>
               <ItemMasterForm
                  isOpen={isOpen}
                  onClose={onClose}
                  isEditMode={isEditMode}
                  currentRow={currentRow}
                  onSave={handleSave}
                  currentPage={currentPage}
                  limit={limit}
                  searchQuery={searchQuery}
                  sorting={sorting}
                  pagination={pagination}
                  isAbs={isAbs}
               />
            </React.Suspense>

            <DeleteDialog
               isDeleteDialogOpen={isDeleteDialogOpen}
               setDeleteDialogOpen={setDeleteDialogOpen}
               handleDelete={handleDelete}
               title="Item"
            />

            {singleEntity && Object.keys(singleEntity)?.length > 0 && (
               <ViewSingleData
                  singleEntityRights={itemRight}
                  isOpenSingleView={isOpenSingleView}
                  onClose={() => setIsOpenSingleView(false)}
                  entityType="item"
                  singleEntity={singleEntity}
                  dynamicArray={dynamicArray}
                  formType="Item"
                  reCall={(id) => handleViewSingleData(id)}
               />
            )}

            {isMobileOrTablet && (Admin || itemRight?.add) && user && !user.isExpired && (
               <Box position="fixed" bottom="4" right="4" zIndex={999}>
                  <button
                     onClick={() => {
                        setCurrentRow({});
                        setIsEditMode(false);
                        onOpen();
                     }}
                     className={`group relative flex items-center justify-center gap-3 py-2 px-7 rounded-xl font-medium shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 overflow-hidden border ${isDark
                           ? 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-400/40 focus:ring-cyan-200/50 border-cyan-300/30'
                           : 'bg-gradient-to-br from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-white shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-400/40 focus:ring-emerald-200/70 border-emerald-300/50'
                        }`}>
                     {/* Gradient overlay on hover */}
                     <span
                        className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out ${isDark ? 'via-cyan-100/20' : 'via-white/30'
                           }`}></span>
                     {/* Icon container with background */}
                     <span
                        className={`relative flex items-center justify-center w-6 h-6 rounded-lg backdrop-blur-sm transition-all duration-300 ${isDark ? 'bg-cyan-100/20 group-hover:bg-cyan-100/30' : 'bg-white/20 group-hover:bg-white/30'
                           }`}>
                        <svg
                           className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                        </svg>
                     </span>
                     <span className="relative text-base font-semibold">Add New</span>
                  </button>
               </Box>
            )}

            <Modal
               isOpen={isImportModalOpen}
               onClose={() => {
                  setIsImportModalOpen(false);
                  setExcelFile(null);
               }}
               isCentered
               motionPreset="scale">
               <ModalOverlay backdropFilter="blur(4px)" />
               <ModalContent borderRadius="2xl" boxShadow="2xl">
                  <ModalHeader textAlign="center">
                     <Icon as={BsFileEarmarkExcel} boxSize={10} color="green.500" mb={2} />
                     Import Item Excel
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                     <VStack spacing={5}>
                        <Text fontSize="sm" color="gray.600" textAlign="center">
                           Upload your Excel file (.xls or .xlsx) to import item data
                        </Text>
                        <Box
                           w="100%"
                           p={4}
                           bg="gray.50"
                           border="1px solid"
                           borderColor="gray.200"
                           borderRadius="xl"
                           display="flex"
                           alignItems="center"
                           justifyContent="space-between">
                           <Box>
                              <Text fontSize="sm" fontWeight="semibold">
                                 Need a sample Excel format?
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                 Download a demo Excel file to see the Excel format
                              </Text>
                           </Box>
                           <Button
                              size="sm"
                              variant="outline"
                              colorScheme="green"
                              leftIcon={<BsFileEarmarkExcel />}
                              rightIcon={<DownloadIcon />}
                              onClick={handleDownloadDemoExcel}>
                              Download
                           </Button>
                        </Box>
                        {/* Upload Box */}
                        <Box
                           w="100%"
                           p={6}
                           border="2px dashed"
                           borderColor="green.400"
                           borderRadius="xl"
                           textAlign="center"
                           cursor="pointer"
                           transition="all 0.3s ease"
                           _hover={{
                              bg: 'green.50',
                              borderColor: 'green.500',
                              transform: 'scale(1.02)',
                           }}
                           position="relative">
                           <Icon as={FiUploadCloud} boxSize={8} color="green.500" mb={2} />
                           <Text fontWeight="medium">Click to upload Excel file</Text>
                           <Text fontSize="xs" color="gray.500">
                              Only .xls & .xlsx files are supported
                           </Text>
                           <input
                              type="file"
                              accept=".xls,.xlsx"
                              onChange={(e) => handleExcelUpload(e.target.files[0])}
                              style={{
                                 position: 'absolute',
                                 inset: 0,
                                 opacity: 0,
                                 cursor: 'pointer',
                              }}
                           />
                        </Box>
                        {/* Selected file preview */}
                        {excelFile && (
                           <Fade in>
                              <Box
                                 w="100%"
                                 p={3}
                                 bg="green.50"
                                 borderRadius="lg"
                                 border="1px solid"
                                 borderColor="green.200">
                                 <Text fontSize="sm" color="green.700">
                                    📄 {excelFile.name}
                                 </Text>
                              </Box>
                           </Fade>
                        )}
                        {/* <Input type="file" accept=".xls,.xlsx" onChange={(e) => handleExcelUpload(e.target.files[0])} /> */}
                     </VStack>
                  </ModalBody>
                  <ModalFooter justifyContent="space-between">
                     <Button
                        variant="ghost"
                        mr={3}
                        onClick={() => {
                           setIsImportModalOpen(false);
                           setExcelFile(null);
                        }}>
                        Cancel
                     </Button>
                     <Button
                        colorScheme="green"
                        leftIcon={<BsFileEarmarkExcel />}
                        onClick={handleImportExcel}
                        isDisabled={!excelFile}
                        // isLoading={isImporting}
                        loadingText="Importing..."
                        px={6}>
                        Import
                     </Button>
                  </ModalFooter>
               </ModalContent>
            </Modal>
         </Box>

         <ColumnVisibilityModal
            isOpen={isColumnModalOpen}
            onClose={() => setIsColumnModalOpen(false)}
            columnVisibility={columnVisibility}
            setColumnVisibility={setColumnVisibility}
            user={user}
         />
      </>
   );
}
